import type { ReactNode } from 'react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { createContext, useContext, useState } from 'react';
import { ESUSU_BASE_URL, esusuConfig } from '../utils/esusu';
import { useRentReportingAuth } from './RentReportingAuthProvider';
import { getPublicKeyForEnv } from '../utils/esusu';

interface EsusuContextType {
  esusuSdk: unknown | null;
  encodedZuid: string | null;
  setShouldInitializeEsusu: (shouldInitialize: boolean) => void;
  setIsEsusuScriptLoaded: (isEsusuScriptLoaded: boolean) => void;
  isEsusuScriptLoaded: boolean;
  markContainerAsInitialized: (containerId: string) => void;
  isContainerInitialized: (containerId: string) => boolean;
}

const EsusuContext = createContext<EsusuContextType | undefined>(undefined);

export const useEsusu = () => {
  const context = useContext(EsusuContext);
  if (context === undefined) {
    throw new Error('useEsusu must be used within an EsusuProvider');
  }
  return context;
};

interface EsusuProviderProps {
  children: ReactNode;
}

export const EsusuProvider: React.FC<EsusuProviderProps> = ({ children }) => {
  const { user } = useRentReportingAuth();
  const [esusuSdk, setEsusuSdk] = useState<unknown | null>(null);
  const [shouldInitializeEsusu, setShouldInitializeEsusu] = useState(false);
  const [isEsusuScriptLoaded, setIsEsusuScriptLoaded] = useState(false);

  const initializedContainersRef = useRef<Set<string>>(new Set());

  const encodedZuid = user?.encodedZuid || null;

  const initializeEsusu = (encodedZuid: string) => {
    if (typeof window !== 'undefined' && window.Esusu) {
      const esusuKey = getPublicKeyForEnv();
      const sdk = new window.Esusu({
        publicKey: esusuKey,
        referenceId: encodedZuid,
        language: 'en',
        baseUrl: ESUSU_BASE_URL,
        config: esusuConfig,
        refreshSession: false,
      });
      setEsusuSdk(sdk);
    }
  };

  useEffect(() => {
    if (encodedZuid && shouldInitializeEsusu) {
      initializeEsusu(encodedZuid);
    }
  }, [shouldInitializeEsusu, encodedZuid]);

  // When SDK is recreated (e.g., user changes), reset initialized containers
  useEffect(() => {
    if (esusuSdk === null) {
      initializedContainersRef.current = new Set();
    }
  }, [esusuSdk]);

  // Memoize callbacks to prevent unnecessary effect re-runs in consumers
  const markContainerAsInitialized = useCallback((containerId: string) => {
    initializedContainersRef.current.add(containerId);
  }, []);

  const isContainerInitialized = useCallback((containerId: string) => {
    return initializedContainersRef.current.has(containerId);
  }, []);

  const value: EsusuContextType = {
    esusuSdk,
    encodedZuid: encodedZuid,
    setShouldInitializeEsusu,
    setIsEsusuScriptLoaded,
    isEsusuScriptLoaded,
    markContainerAsInitialized,
    isContainerInitialized,
  };

  return <EsusuContext.Provider value={value}>{children}</EsusuContext.Provider>;
};
